/**
 * @typedef {Object} CityCacheEntry
 * @property {string} city
 * @property {number} ts
 */

const STORAGE_KEY = 'delivery_city';
const TTL_MS = 24 * 60 * 60 * 1000;
const FETCH_TIMEOUT_MS = 4000;

/** @type {string | null} */
let resolvedCity = null;
/** @type {Promise<void> | null} */
let fetchPromise = null;

/** @returns {CityCacheEntry | null} */
const readCache = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
  } catch (_) {
    return null;
  }
};

/** @param {string} city */
const writeCache = (city) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ city, ts: Date.now() }));
  } catch (_) {}
};

const refreshAll = () => {
  for (const el of document.querySelectorAll('delivery-city')) {
    if (el instanceof DeliveryCity) el.refresh();
  }
};

/** @returns {Promise<void>} */
const fetchCity = () => {
  if (fetchPromise) return fetchPromise;

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = setTimeout(() => controller?.abort(), FETCH_TIMEOUT_MS);

  fetchPromise = fetch('https://ipapi.co/json/', controller ? { signal: controller.signal } : undefined)
    .then((res) => {
      if (!res.ok) throw new Error(String(res.status));
      return res.json();
    })
    .then((data) => {
      if (data?.city) {
        resolvedCity = String(data.city);
        writeCache(resolvedCity);
        refreshAll();
      }
    })
    .catch(() => {})
    .finally(() => {
      clearTimeout(timeoutId);
      fetchPromise = null;
    });

  return fetchPromise;
};

class DeliveryCity extends HTMLElement {
  connectedCallback() {
    if (!resolvedCity) {
      const cached = readCache();
      if (cached?.city) {
        resolvedCity = cached.city;
        const isStale = Date.now() - cached.ts >= TTL_MS;
        if (isStale) fetchCity();
      } else {
        fetchCity();
      }
    }

    this.refresh();
  }

  refresh() {
    if (resolvedCity && this.textContent !== resolvedCity) {
      this.textContent = resolvedCity;
    }
  }
}

if (!customElements.get('delivery-city')) {
  customElements.define('delivery-city', DeliveryCity);
}
